import { ref } from 'vue'
import { collection, query, where, getDocs, doc, updateDoc, addDoc, deleteDoc, orderBy } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { TIME_SLOTS, TIME_SLOT_GROUPS, getSlotsByPeriod } from '@/utils/timeSlots'

// 세션스토리지 키
const SPLASH_SESSION_KEY = 'splash_shown_session'

export function useSplashAd() {
  const loading = ref(false)
  const error = ref(null)

  // 현재 시간대 가져오기
  const getCurrentTimeSlot = () => {
    const now = new Date()
    const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
    const hours = koreaTime.getHours()
    const minutes = koreaTime.getMinutes()
    const currentTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`

    return TIME_SLOTS.find(slot => {
      return currentTime >= slot.start && currentTime < slot.end
    }) || TIME_SLOTS[0]
  }

  // 세션에서 이미 봤는지 확인
  const hasShownInSession = () => {
    return sessionStorage.getItem(SPLASH_SESSION_KEY) === 'true'
  }

  // 세션에 표시 기록
  const markAsShown = () => {
    sessionStorage.setItem(SPLASH_SESSION_KEY, 'true')
  }

  // 현재 노출 가능한 스플래시 광고 가져오기
  const getActiveSplashAd = async () => {
    // 세션에서 이미 봤으면 null
    if (hasShownInSession()) {
      console.log('[Splash] 세션에서 이미 봄, 스킵')
      return null
    }

    try {
      loading.value = true
      const currentSlot = getCurrentTimeSlot()
      console.log('[Splash] 현재 시간대:', currentSlot)

      // 승인된 스플래시 광고 전체 조회 후 필터링
      // (timeSlot.id 타입 불일치 문제 방지)
      const q = query(
        collection(db, 'splashAds'),
        where('status', '==', 'approved')
      )

      const snapshot = await getDocs(q)
      console.log('[Splash] 승인된 광고 수:', snapshot.size)

      if (snapshot.empty) {
        console.log('[Splash] 승인된 광고 없음')
        return null
      }

      // 현재 시간대에 맞는 광고 필터링
      const allAds = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      console.log('[Splash] 전체 광고:', allAds.map(ad => ({
        id: ad.id,
        timeSlot: ad.timeSlot,
        title: ad.title
      })))

      // 시간대 매칭 (start/end 기준)
      const now = new Date()
      const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
      const currentTime = `${String(koreaTime.getHours()).padStart(2, '0')}:${String(koreaTime.getMinutes()).padStart(2, '0')}`

      const matchingAds = allAds.filter(ad => {
        if (!ad.timeSlot) return false
        const { start, end } = ad.timeSlot
        const isMatch = currentTime >= start && currentTime < end
        console.log(`[Splash] 광고 "${ad.title}" 시간대 ${start}-${end}, 현재 ${currentTime}, 매칭: ${isMatch}`)
        return isMatch
      })

      if (matchingAds.length === 0) {
        console.log('[Splash] 현재 시간대에 맞는 광고 없음')
        return null
      }

      // 랜덤으로 하나 선택
      const randomIndex = Math.floor(Math.random() * matchingAds.length)
      console.log('[Splash] 선택된 광고:', matchingAds[randomIndex])
      return matchingAds[randomIndex]
    } catch (err) {
      console.error('스플래시 광고 로드 실패:', err)
      error.value = err.message
      return null
    } finally {
      loading.value = false
    }
  }

  // 노출 수 증가
  const incrementImpressions = async (splashId) => {
    try {
      const splashRef = doc(db, 'splashAds', splashId)
      const snapshot = await getDocs(query(
        collection(db, 'splashAds'),
        where('__name__', '==', splashId)
      ))

      if (!snapshot.empty) {
        const currentData = snapshot.docs[0].data()
        await updateDoc(splashRef, {
          impressions: (currentData.impressions || 0) + 1
        })
      }
    } catch (err) {
      console.error('노출 수 증가 실패:', err)
    }
  }

  // 클릭 수 증가
  const incrementClicks = async (splashId) => {
    try {
      const splashRef = doc(db, 'splashAds', splashId)
      const snapshot = await getDocs(query(
        collection(db, 'splashAds'),
        where('__name__', '==', splashId)
      ))

      if (!snapshot.empty) {
        const currentData = snapshot.docs[0].data()
        await updateDoc(splashRef, {
          clicks: (currentData.clicks || 0) + 1
        })
      }
    } catch (err) {
      console.error('클릭 수 증가 실패:', err)
    }
  }

  // 스플래시 광고 신청 (파트너용)
  const applySplashAd = async (data) => {
    try {
      loading.value = true
      const now = new Date().toISOString()

      const splashData = {
        ...data,
        status: 'pending',
        impressions: 0,
        clicks: 0,
        createdAt: now,
        updatedAt: now
      }

      const docRef = await addDoc(collection(db, 'splashAds'), splashData)
      return docRef.id
    } catch (err) {
      console.error('스플래시 광고 신청 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 내 스플래시 광고 목록 (파트너용)
  const getMySplashAds = async (partnerId) => {
    try {
      loading.value = true
      const q = query(
        collection(db, 'splashAds'),
        where('partnerId', '==', partnerId),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('내 스플래시 광고 로드 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  // 스플래시 광고 취소 (파트너용)
  const cancelSplashAd = async (splashId) => {
    try {
      loading.value = true
      await deleteDoc(doc(db, 'splashAds', splashId))
    } catch (err) {
      console.error('스플래시 광고 취소 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 전체 스플래시 광고 목록 (어드민용)
  const getAllSplashAds = async () => {
    try {
      loading.value = true
      const q = query(
        collection(db, 'splashAds'),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('스플래시 광고 목록 로드 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  // 스플래시 광고 상태 변경 (어드민용)
  const updateSplashAdStatus = async (splashId, status, rejectReason = null) => {
    try {
      loading.value = true
      const now = new Date().toISOString()

      const updateData = {
        status,
        updatedAt: now
      }

      if (status === 'approved') {
        updateData.approvedAt = now
      } else if (status === 'rejected' && rejectReason) {
        updateData.rejectReason = rejectReason
        updateData.rejectedAt = now
      }

      await updateDoc(doc(db, 'splashAds', splashId), updateData)
    } catch (err) {
      console.error('상태 변경 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 기존 신청 확인 (파트너용)
  const checkExistingSplashAd = async (partnerId) => {
    try {
      // pending 상태 확인
      const pendingQ = query(
        collection(db, 'splashAds'),
        where('partnerId', '==', partnerId),
        where('status', '==', 'pending')
      )
      const pendingSnapshot = await getDocs(pendingQ)
      if (!pendingSnapshot.empty) return true

      // approved 상태 확인
      const approvedQ = query(
        collection(db, 'splashAds'),
        where('partnerId', '==', partnerId),
        where('status', '==', 'approved')
      )
      const approvedSnapshot = await getDocs(approvedQ)
      return !approvedSnapshot.empty
    } catch (err) {
      console.error('기존 신청 확인 실패:', err)
      return false
    }
  }

  return {
    loading,
    error,
    TIME_SLOTS,
    TIME_SLOT_GROUPS,
    getSlotsByPeriod,
    getCurrentTimeSlot,
    hasShownInSession,
    markAsShown,
    getActiveSplashAd,
    incrementImpressions,
    incrementClicks,
    applySplashAd,
    getMySplashAds,
    cancelSplashAd,
    getAllSplashAds,
    updateSplashAdStatus,
    checkExistingSplashAd
  }
}
